import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount } from 'wagmi';
import { formatUnits } from 'viem';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Button } from "@/components/button";
import {Card, CardContent, CardFooter, CardHeader, CardTitle} from "@/components/card";
import { Input } from "@/components/input";
import {
  RiLoader4Line,
  RiFileCopyLine,
  RiExternalLinkLine,
  RiSearchLine,
} from "@remixicon/react";
import { BRIDGE_NETWORKS } from "@/configs/bridge.config";
import { useBridge } from "@/providers/global-data.provider";

interface FilteredTransaction {
  id: string;
  sourceHash?: string;
  destinationHash?: string;
  sourceChain: string;
  destinationChain: string;
  fromAddress?: string;
  toAddress?: string;
  token: string;
  amount: string;
  status: string;
  statusText: string;
  timestamp: number;
  formattedDate: string;
  sourceChainInfo?: {
    name: string;
    icon?: string;
    explorerLink?: string;
  };
  destChainInfo?: {
    name: string;
    icon?: string;
    explorerLink?: string;
  };
  tokenInfo?: {
    symbol: string;
    decimals?: number;
    logoURI?: string;
  };
}

export default function BridgeScanPage() {
  const { address, isConnected } = useAccount();
  const { chains, isLoadingChains, fetchBridgeTransactions } = useBridge();

  // Filter states
  const [chainFilter, setChainFilter] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [addressFilter, setAddressFilter] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");

  // Data states
  const [transactions, setTransactions] = useState<FilteredTransaction[]>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 50;

  // Get network info helper
  const getNetworkInfo = (chainName: string) => {
    const network = BRIDGE_NETWORKS.find(n => n.name === chainName);
    return network ? {
      name: network.name,
      icon: network.iconURI,
    } : { name: chainName };
  };

  // Get token info helper
  const getTokenInfo = (tokenSymbol: string) => {
    for (const network of BRIDGE_NETWORKS) {
      const token = network.tokens.find(t => t.symbol === tokenSymbol);
      if (token) {
        return {
          symbol: token.symbol,
          decimals: token.decimals,
          logoURI: token.logoURI,
        };
      }
    }
    return { symbol: tokenSymbol };
  };

  // Format status text
  const getStatusText = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'Executed';
      case 'pending':
        return 'Active';
      case 'failed':
      case 'error':
        return 'Cancelled';
      default:
        return 'Active';
    }
  };

  // Get status class for styling
  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      '0': 'sent',
      '1': 'active',
      '2': 'active',
      '3': 'executed',
      '4': 'cancelled',
      '5': 'active'
    };
    return statusMap[status] || 'active';
  };

  // Load transactions from API
  const loadTransactions = useCallback(async () => {
    setIsLoadingTransactions(true);
    setTransactionsError(null);

    try {
      const result = await fetchBridgeTransactions({
        page: currentPage,
        limit: limit,
        sourceChain: chainFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        status: statusFilter || undefined,
        address: addressFilter.trim() || undefined,
        search: searchInput.trim() || undefined,
      });

      if (!result.status) {
        setTransactionsError(result.message || 'Error loading transactions');
        setTransactions([]);
        setTotalTransactions(0);
        setTotalPages(1);
        return;
      }

      // Transform API response to match component format
      const transformedTransactions: FilteredTransaction[] = result.data.map((tx: any) => ({
        id: tx.id || '',
        sourceHash: tx.sourceTxHash,
        destinationHash: tx.destinationHash || tx.destTxHash,
        sourceChain: tx.sourceChain?.name || 'Unknown',
        destinationChain: tx.destChain?.name || 'Unknown',
        fromAddress: tx.fromAddress,
        toAddress: tx.toAddress,
        token: tx.token?.symbol || 'Unknown',
        amount: tx.amount || '0',
        status: tx.status || '0',
        statusText: tx.statusText || 'Active',
        timestamp: tx.timestamp || Date.now() / 1000,
        formattedDate: tx.formattedDate || new Date().toLocaleString(),
        sourceChainInfo: {
          name: tx.sourceChain?.name || 'Unknown',
          icon: tx.sourceChain?.icon,
          explorerLink: tx.sourceChain?.explorerLink,
        },
        destChainInfo: {
          name: tx.destChain?.name || 'Unknown',
          icon: tx.destChain?.icon,
          explorerLink: tx.destChain?.explorerLink,
        },
        tokenInfo: tx.token,
      }));

      setTransactions(transformedTransactions);
      setTotalTransactions(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch (err: any) {
      setTransactionsError(err.message || 'Failed to load transactions');
      setTransactions([]);
      setTotalTransactions(0);
      setTotalPages(1);
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [fetchBridgeTransactions, currentPage, chainFilter, startDate, endDate, statusFilter, addressFilter, searchInput]);

  // Format amount
  const formatAmount = (amount: string, decimals: number = 18): string => {
    if (!amount) return '0';
    try {
      // Check if amount is already in decimal format (contains a dot)
      let num: number;
      if (amount.includes('.')) {
        num = parseFloat(amount);
      } else {
        // Assume it's in wei/smallest unit, convert using formatUnits
        try {
          num = parseFloat(formatUnits(BigInt(amount), decimals));
        } catch {
          // If BigInt conversion fails, try parsing as regular number
          num = parseFloat(amount) / Math.pow(10, decimals);
        }
      }

      if (num >= 1000000) {
        return (num / 1000000).toFixed(2) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(2) + 'K';
      }
      return num.toLocaleString('en-US', { maximumFractionDigits: 8, minimumFractionDigits: 0 });
    } catch {
      return amount;
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get explorer URL
  const getExplorerUrl = (explorerLink: string | undefined, txHash: string, chainName?: string, isAddress?: boolean): string => {
    if (explorerLink && explorerLink.includes('{hash}')) {
      return explorerLink.replace('{hash}', txHash).replace('{txHash}', txHash);
    }
    if (explorerLink) {
      return isAddress ? `${explorerLink}/address/${txHash}` : `${explorerLink}/tx/${txHash}`;
    }
    // Fallback to BRIDGE_NETWORKS
    const network = BRIDGE_NETWORKS.find(n => n.name === chainName);
    return network ? `${network.scanURL}/tx/${txHash}` : '#';
  };

  // Reset filters
  const resetFilters = () => {
    setChainFilter("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("");
    setAddressFilter("");
    setSearchInput("");
    setCurrentPage(1);
  };

  // Load transactions when filters or page changes
  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [chainFilter, startDate, endDate, statusFilter, addressFilter, searchInput]);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-7xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/blockchain">Blockchain</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Bridge Scan</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              {/* Filters */}
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex flex-col gap-2 min-w-[150px]">
                      <label className="text-sm text-muted-foreground">Chain:</label>
                      <div className="relative">
                        <select
                          value={chainFilter}
                          onChange={(e) => setChainFilter(e.target.value)}
                          className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={isLoadingChains}
                        >
                          <option value="">All chains</option>
                          {chains.map((chain) => (
                            <option key={chain.id} value={chain.id.toString()}>
                              {chain.name}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 min-w-[150px]">
                      <label className="text-sm text-muted-foreground">Bridge status:</label>
                      <div className="relative">
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm appearance-none cursor-pointer"
                        >
                          <option value="">All</option>
                          <option value="0">Inactive</option>
                          <option value="1">Active</option>
                          <option value="2">Passed</option>
                          <option value="3">Executed</option>
                          <option value="4">Cancelled</option>
                          <option value="5">HashPending</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 min-w-[200px]">
                      <label className="text-sm text-muted-foreground">Address:</label>
                      <Input
                        type="text"
                        placeholder="0x..."
                        value={addressFilter}
                        onChange={(e) => setAddressFilter(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            setCurrentPage(1);
                          }
                        }}
                      />
                    </div>

                    <div className="flex flex-col gap-2 min-w-[250px] ml-auto">
                      <label className="text-sm text-muted-foreground">Search by tx hash:</label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Search by tx hash"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              setCurrentPage(1);
                            }
                          }}
                        />
                        <Button
                          onClick={() => setCurrentPage(1)}
                          className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                        >
                          <RiSearchLine className="size-4" />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={resetFilters}
                        >
                          Reset
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Table */}
              <Card className="p-0 gap-0">
                <CardContent className="p-0">
                  {isLoadingTransactions ? (
                    <div className="flex items-center justify-center py-20">
                      <RiLoader4Line className="size-8 animate-spin text-muted-foreground" />
                      <span className="ml-3 text-muted-foreground">Loading transactions...</span>
                    </div>
                  ) : transactionsError ? (
                    <div className="p-6 bg-red-500/10 border-t border-red-500/20">
                      <p className="text-red-500">{transactionsError}</p>
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20">
                      <div className="text-5xl mb-4">📭</div>
                      <div className="text-muted-foreground">No transactions found</div>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-muted/50 border-b">
                            <tr>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Source txn hash</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">From address/token</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Bridge status</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Bridge direction</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">To address/token</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Destination txn hash</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((tx) => (
                              <tr key={tx.id} className="border-b hover:bg-muted/30 transition-colors">
                                <td className="px-6 py-4">
                                  {tx.sourceHash ? (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <a
                                          href={getExplorerUrl(tx.sourceChainInfo?.explorerLink, tx.sourceHash, tx.sourceChain)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-[#FF8A3C] hover:underline font-mono text-sm"
                                        >
                                          {tx.sourceHash.substring(0, 10)}...
                                        </a>
                                        <button
                                          onClick={() => copyToClipboard(tx.sourceHash!)}
                                          className="text-muted-foreground hover:text-[#FF8A3C] transition-colors"
                                          title="Copy"
                                        >
                                          <RiFileCopyLine className="size-4" />
                                        </button>
                                      </div>
                                      <div className="text-xs text-muted-foreground mt-1">
                                        {tx.formattedDate}
                                      </div>
                                      {tx.sourceChainInfo && (
                                        <div className="flex items-center gap-1 mt-1">
                                          {tx.sourceChainInfo.icon && (
                                            <img
                                              src={tx.sourceChainInfo.icon}
                                              alt={tx.sourceChainInfo.name}
                                              className="w-4 h-4 rounded-full"
                                            />
                                          )}
                                          <span className="text-xs text-muted-foreground">{tx.sourceChainInfo.name}</span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-muted-foreground">N/A</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  {tx.fromAddress ? (
                                      <>
                                        <div className="flex items-center gap-2">
                                          <a
                                              href={getExplorerUrl(tx.sourceChainInfo?.explorerLink, tx.fromAddress, tx.sourceChain, true)}
                                              target="_blank"
                                              className="text-[#FF8A3C] hover:underline font-mono text-sm"
                                          >
                                            {tx.fromAddress.substring(0, 6)}...{tx.fromAddress.substring(tx.fromAddress.length - 6)}
                                          </a>
                                          <button
                                              onClick={() => copyToClipboard(tx.fromAddress!)}
                                              className="text-muted-foreground hover:text-[#FF8A3C] transition-colors"
                                              title="Copy"
                                          >
                                            <RiFileCopyLine className="size-4" />
                                          </button>
                                        </div>
                                        {tx.tokenInfo && tx.amount && (
                                            <div className="flex items-center gap-2 mt-2">
                                              {tx.tokenInfo.logoURI && (
                                                  <img
                                                      src={tx.tokenInfo.logoURI}
                                                      alt={tx.tokenInfo.symbol}
                                                      className="w-5 h-5 rounded-full"
                                                  />
                                              )}
                                              <span className="font-medium">
                                            {formatAmount(tx.amount, tx.tokenInfo.decimals || 18)}
                                              </span>
                                                  <span className="text-sm text-muted-foreground">
                                                {tx.tokenInfo.symbol}
                                              </span>
                                            </div>
                                        )}
                                      </>
                                  ) : (
                                      <span className="text-muted-foreground">--</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <span
                                    className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                                      getStatusClass(tx.status) === 'executed'
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : getStatusClass(tx.status) === 'cancelled'
                                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                    }`}
                                  >
                                    {tx.statusText}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    {tx.sourceChainInfo?.icon ? (
                                      <img
                                        src={tx.sourceChainInfo.icon}
                                        alt={tx.sourceChainInfo.name}
                                        className="w-6 h-6 rounded-full"
                                      />
                                    ) : (
                                      <span className="font-medium">{tx.sourceChainInfo?.name || tx.sourceChain}</span>
                                    )}
                                    <span className="text-muted-foreground">→</span>
                                    {tx.destChainInfo?.icon ? (
                                      <img
                                        src={tx.destChainInfo.icon}
                                        alt={tx.destChainInfo.name}
                                        className="w-6 h-6 rounded-full"
                                      />
                                    ) : (
                                      <span className="font-medium">{tx.destChainInfo?.name || tx.destinationChain}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  {tx.toAddress ? (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <a
                                          href={getExplorerUrl(tx.destChainInfo?.explorerLink, tx.toAddress, tx.destinationChain, true)}
                                          className="text-[#FF8A3C] hover:underline font-mono text-sm"
                                          target="_blank"
                                        >
                                          {tx.toAddress.substring(0, 6)}...{tx.toAddress.substring(tx.toAddress.length - 6)}
                                        </a>
                                        <button
                                          onClick={() => copyToClipboard(tx.toAddress!)}
                                          className="text-muted-foreground hover:text-[#FF8A3C] transition-colors"
                                          title="Copy"
                                        >
                                          <RiFileCopyLine className="size-4" />
                                        </button>
                                      </div>
                                      {tx.tokenInfo && tx.amount && (
                                        <div className="flex items-center gap-2 mt-2">
                                          {tx.tokenInfo.logoURI && (
                                            <img
                                              src={tx.tokenInfo.logoURI}
                                              alt={tx.tokenInfo.symbol}
                                              className="w-5 h-5 rounded-full"
                                            />
                                          )}
                                          <span className="font-medium">
                                            {formatAmount(tx.amount, tx.tokenInfo.decimals || 18)}
                                          </span>
                                          <span className="text-sm text-muted-foreground">
                                            {tx.tokenInfo.symbol}
                                          </span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-muted-foreground">--</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  {tx.destinationHash ? (
                                      <>
                                        <div className="flex items-center gap-2">
                                          <a
                                              href={getExplorerUrl(tx.destChainInfo?.explorerLink, tx.destinationHash, tx.destinationChain)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-[#FF8A3C] hover:underline font-mono text-sm"
                                          >
                                            {tx.destinationHash.substring(0, 10)}...
                                          </a>
                                          <button
                                              onClick={() => copyToClipboard(tx.destinationHash!)}
                                              className="text-muted-foreground hover:text-[#FF8A3C] transition-colors"
                                              title="Copy"
                                          >
                                            <RiFileCopyLine className="size-4" />
                                          </button>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                          {tx.formattedDate}
                                        </div>
                                        {tx.destChainInfo && (
                                            <div className="flex items-center gap-1 mt-1">
                                              {tx.destChainInfo.icon && (
                                                  <img
                                                      src={tx.destChainInfo.icon}
                                                      alt={tx.destChainInfo.name}
                                                      className="w-4 h-4 rounded-full"
                                                  />
                                              )}
                                              <span className="text-xs text-muted-foreground">{tx.destChainInfo.name}</span>
                                            </div>
                                        )}
                                      </>
                                  ) : (
                                      <span className="text-muted-foreground">N/A</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-4 p-6 border-t">
                          <Button
                            variant="outline"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            Page {currentPage} of {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage >= totalPages}
                          >
                            Next
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
                <CardFooter className="px-6 py-2 border-t">
                  <div className="text-sm text-muted-foreground">
                    {isLoadingTransactions ? (
                        "Loading..."
                    ) : transactions.length === 0 ? (
                        "No transactions found"
                    ) : (
                        <>
                          Total {totalTransactions.toLocaleString()} transactions
                          {transactions.length > 0 && (
                              <> (Showing {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, totalTransactions)} of {totalTransactions.toLocaleString()})</>
                          )}
                        </>
                    )}
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
